<template>
  <div class="market-project">
    <div
      v-if="loadingProjects && !marketplaceProjects.length"
      class="d-flex justify-content-center p-2"
    >
      <div class="loader"></div>
    </div>

    <div v-else class="offers">
      <BModal
        ref="offerFormRequest"
        class="modal-request-offer"
        id="offerFormRequest"
        size="xl"
        hide-header
        hide-footer
        no-enforce-focus
      >
        <OfferFormRequest
          :marketplaceId="selectedMarketplaceId"
          :formId="formId"
          :isNewOffer="true"
          :projectId="selectedProjectId"
          @onCreatedOffer="onCreatedOffer"
          @onSaveOffer="onSaveOffer"
          @close="closeModal"
        />
      </BModal>

      <div v-if="showStatusFilter" class="offers-status mb-4">
        <h3 v-if="isBuyer">
          You have: <strong>{{ buyerStats.total }}</strong> RFQ's,
          <strong>{{ buyerStats.offersReceived }}</strong> offers received,
          <strong>{{ buyerStats.deadlinePast }}</strong> deadline past
        </h3>
        <h3 v-else>
          You have: <strong>{{ sellerStats.total }}</strong> projects
        </h3>

        <div class="offers-status__filters">
          <div class="d-flex flex-wrap">
            <label
              v-for="opt in statusFilterOptions"
              :key="opt.value"
              @click.stop.prevent="setStatusFilter(opt.value)"
            >
              <span
                class="status"
                :class="[opt.className, { active: statusFilter === opt.value }]"
              >
                {{ opt.label }}
              </span>
            </label>
          </div>
        </div>
      </div>

      <div v-if="!isBuyer" class="subscribe">
        <label class="subscribe__label">
          Subscribe to new projects within this filter:
          <input
            v-model="subscribeToFilter"
            type="checkbox"
            class="subscribe__checkbox"
            :disabled="subscriptionBusy"
          >
          <span class="subscribe__custom-check" />
        </label>
        <div v-if="subscriptionFeedback" class="subscribe__feedback">
          {{ subscriptionFeedback }}
        </div>
      </div>

      <template v-if="!marketplaceProjects.length && !isFiltered">
        <div v-if="isBuyer">
          <div class="no-content">
            <p>
              Here is <strong>the example project</strong>, <br />
              but you can
              <a
                class="link"
                href="#"
                @click.stop.prevent="$emit('onRequestOffer')"
              >
                Request your own
              </a>.
            </p>
          </div>

          <div class="market-project__list">
            <div class="market-project__item offered">
              <div class="market-project__content">
                <h3 class="market-project__title">
                  This is just an example project. To get quotes for your project select a market
                  place and click the request offer button. You can choose all the details like
                  country and category there. If you need help go to
                  <a
                    href="https://www.wethink.eu/groups/energy-transition-market"
                    target="_blank"
                    rel="noopener noreferrer"
                    @click.stop
                  >
                    FAQ
                  </a>
                  and write us a message.
                </h3>
                <ul class="market-project__category">
                  <li>
                    <a href="#" @click.stop.prevent>
                      Austria
                    </a>
                  </li>
                  <li>
                    <a href="#" @click.stop.prevent>
                      Wind solar PV EPC
                    </a>
                  </li>
                </ul>
              </div>
              <div class="market-project__actions">
                <div class="market-project__info offer">
                  <strong>4</strong> offers from Sellers
                </div>
                <div class="market-project__buttons">
                  <a class="btn-action" href="#" @click.stop.prevent="$emit('onRequestOffer')">
                    See Offers
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else>
          <div class="no-content" v-if="marketplace">
            <p>
              There are no projects in the selected Marketplace <strong>{{ marketplace.name }}</strong>.
            </p>
            <p>But you can go through other Marketplaces.</p>
          </div>

          <div v-else class="no-content">
            <p>
              There are no projects in the selected Marketplace.
            </p>
            <p>But you can go through other Marketplaces.</p>
          </div>
        </div>
      </template>

      <template v-else-if="!marketplaceProjects.length && isFiltered">
        <div class="market-project__parameters no-content">
          <p>Sorry, <strong>no projects found matching your search criteria.</strong></p>
          <p>
            Please try adjusting your search terms for better results. Or you can
            <a class="link" href="#" @click.stop.prevent="$emit('onRequestOffer')">
              Request your own.
            </a>
          </p>
        </div>
      </template>

      <template v-else-if="marketplaceProjects.length && !filteredMarketplaceProjects.length">
        <div class="market-project__parameters no-content">
          <p>Sorry, <strong>no projects found for the selected status.</strong></p>
          <p>Please try selecting a different status.</p>
        </div>
      </template>

      <template v-else>
        <div class="market-project__parameters">
          <p v-if="filteredMarketplaceProjects.length > 1">
            <strong>
              {{ filteredMarketplaceProjects.length }}
            </strong> projects with such parameters are shown
          </p>
          <p v-else>
            <strong>
              {{ filteredMarketplaceProjects.length }}
            </strong> project with such parameters is shown
          </p>

          <p v-if="marketplace">
            Marketplace: <strong>{{ marketplace.name }}</strong>
          </p>
          <p v-else>
            Marketplace: <strong>All</strong>
          </p>

          <p v-if="selectedCountries.length">
            Countries: <strong>{{ selectedCountries.join(', ') }}</strong>
          </p>

          <div v-if="marketplace">
            <div v-for="(category, index) in marketplace.filters" :key="index">
              <div v-if="index !== 'countries'">
                <div v-if="category.type === 'dropbox'">
                  <div v-if="category.multiple">
                    <div v-for="(value, idx) in category.initValue" :key="idx">
                      <div v-if="category.values[value]">
                        {{ category.name }}: <strong>{{ category.values[value] }}</strong>
                      </div>
                    </div>
                  </div>

                  <div v-else>
                    <div v-if="dynamycDropboxes[index] && dynamycDropboxes[index].length">
                      {{ category.name }}:
                      <span v-for="(value, idx) in dynamycDropboxes[index]" :key="idx">
                        <span v-if="category.values[value]">
                          <strong>{{ category.values[value] }}</strong>
                          <span v-if="idx !== dynamycDropboxes[index].length - 1">, </span>
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                <div v-else>
                  <div v-if="dynamycInputs[index]">
                    {{ category.name }}: <strong>{{ dynamycInputs[index] }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else>
            <div v-for="mp in marketplaces" :key="mp.id">
              <div v-for="(category, index) in mp.filters" :key="index">
                <div v-if="index !== 'countries'">
                  <div v-if="category.type === 'dropbox'">
                    <div v-if="category.multiple">
                      <div v-for="(value, idx) in category.initValue" :key="idx">
                        <div v-if="category.values[value]">
                          {{ category.name }}: <strong>{{ category.values[value] }}</strong>
                        </div>
                      </div>
                    </div>

                    <div v-else>
                      <div v-if="dynamycDropboxes[index] && dynamycDropboxes[index].length">
                        {{ category.name }}:
                        <span v-for="(value, idx) in dynamycDropboxes[index]" :key="idx">
                          <span v-if="category.values[value]">
                            <strong>{{ category.values[value] }}</strong>
                            <span v-if="idx !== dynamycDropboxes[index].length - 1">, </span>
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div v-else>
                    <div v-if="dynamycInputs[index]">
                      {{ category.name }}: <strong>{{ dynamycInputs[index] }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="accordion offers__box" id="accordionOffers">
          <div
            v-for="(item, index) in paginatedMarketplaceProjects"
            :key="item.id || index"
            class="card accordion-multi project-card"
            :class="{ 'project-card--no-accordion': !isBuyer && !hasSellerOffers(item) }"
            :data-project-id="item.id"
            data-panel-group=""
          >
            <div class="card-header" :id="`headingOffers-${item.id || index}`">
              <span
                v-if="isBuyer && unreadMessagesCount(item.id)"
                class="message_badge"
              >
                {{ unreadMessagesCount(item.id) }}
              </span>

              <component
                :is="isProjectExpandable(item) ? 'button' : 'div'"
                v-bind="isProjectExpandable(item) ? { type: 'button' } : {}"
                v-b-toggle="isProjectExpandable(item) ? `collapseProject-${item.id}` : null"
                class="btn-accordion collapsed"
                :class="[
                  accordionClass(item),
                  { 'no-arrow': isBuyer && !isProjectExpandable(item) },
                  { 'project-card__plain': !isBuyer && !isProjectExpandable(item) },
                ]"
                :aria-controls="isProjectExpandable(item) ? `collapseProject-${item.id}` : undefined"
                @click="isBuyer && !isProjectExpandable(item) ? goToProject(item) : () => {}"
              >
                <div class="market-project__content">
                  <h3 class="market-project__title">
                    <a href="#" @click.stop.prevent="goToProject(item)">
                      {{ item.name }}
                    </a>
                  </h3>

                  <div v-if="item.marketplace && item.marketplace.name">
                    Marketplace: <strong>{{ item.marketplace.name }}</strong>
                  </div>

                  <ul class="specs-grid">
                    <li v-if="item.company_country && item.company_country.name">
                      Country: <strong>{{ item.company_country.name }}</strong>
                    </li>

                    <li
                      v-for="spec in projectSpecs(item)"
                      :key="spec.key"
                    >
                      {{ spec.label }}:
                      <strong>{{ spec.value }}</strong>
                    </li>
                  </ul>
                </div>

                <div class="market-project__actions" data-panel-group="">
                  <template v-if="isBuyer">
                    <div
                      class="market-project__info"
                      :class="{ offer: hasProjectOffers(item) || Number(item.status) === 2 }"
                    >
                      <span class="deadline-info">
                        <i class="icon-calendar"></i>Offer deadline:
                        <strong>{{ formatDeadline(item.deadline_at) }}</strong>
                      </span>

                      <template v-if="Number(item.status) === 2">
                        <p class="offer-text">Seller/offer is chosen</p>
                      </template>
                      <template v-else-if="Number(item.status) === 4">
                        <p class="offer-text">Expired without approval</p>
                      </template>
                      <template v-else-if="Number(item.offers_count || 0) === 1">
                        <p class="offer-text">
                          <strong>{{ item.offers_count }}</strong> offer from Seller
                        </p>
                      </template>
                      <template v-else-if="Number(item.offers_count || 0) > 1">
                        <p class="offer-text">
                          <strong>{{ item.offers_count }}</strong> offers from Sellers
                        </p>
                      </template>
                      <template v-else>
                        <p class="offer-text">No offers yet</p>
                      </template>
                    </div>

                    <div class="status-multi">
                      <h6>Status:</h6>
                      <span
                        v-for="status in buyerStatusBadges(item)"
                        :key="status.key"
                        class="status"
                        :class="status.key"
                      >
                        {{ status.label }}
                      </span>
                    </div>

                    <div class="market-project__buttons">
                      <template v-if="Number(item.status) === 2">
                        <a
                          href="#"
                          class="btn-action"
                          @click.stop.prevent="goToProject(item)"
                        >
                          See approved offer
                        </a>
                      </template>

                      <template v-else-if="Number(item.status) === 4">
                        <a
                          href="#"
                          class="btn-action"
                          @click.stop.prevent="goToProject(item)"
                        >
                          Extend deadline?
                        </a>
                        <a
                          v-if="Number(item.offers_count || 0) > 0"
                          href="#"
                          class="btn-action"
                          @click.stop.prevent="goToProject(item)"
                        >
                          Keep talking to sellers
                        </a>
                      </template>

                      <template v-else-if="Number(item.offers_count || 0) > 0">
                        <a
                          href="#"
                          class="btn-action"
                          role="button"
                          tabindex="0"
                          @click.stop.prevent="toggleProjectAccordion($event)"
                        >
                          {{ Number(item.offers_count || 0) === 1 ? 'See Offer' : 'See Offers' }}
                        </a>
                        <a
                          href="#"
                          class="btn-action"
                          @click.stop.prevent="goToProject(item)"
                        >
                          See Details
                        </a>
                      </template>

                      <template v-else>
                        <a
                          href="#"
                          class="btn-action"
                          @click.stop.prevent="goToProject(item)"
                        >
                          See Details
                        </a>
                      </template>

                      <a
                        v-if="getProjectMessagesCount(item) > 0"
                        href="#"
                        class="btn-action"
                        role="button"
                        tabindex="0"
                        @click.stop.prevent="openProjectMessages($event, item)"
                      >
                        {{ projectMessagesButtonLabel(item) }}
                      </a>
                    </div>
                  </template>

                  <template v-else>
                    <div
                      class="market-project__info"
                      :class="{ offer: hasSellerOffers(item) }"
                    >
                      <span class="deadline-info">
                        <i class="icon-calendar"></i>Offer deadline:
                        <strong>{{ formatDeadline(item.deadline_at) }}</strong>
                      </span>

                      <p v-if="hasSellerOffers(item) && Number(item.offers_count || 0) === 1">
                        <strong>My {{ item.offers_count }}</strong> offer
                      </p>

                      <p v-else-if="hasSellerOffers(item) && Number(item.offers_count || 0) > 1">
                        <strong>My {{ item.offers_count }}</strong> offers
                      </p>

                      <p v-else class="no-offer-text">
                        No offers yet
                      </p>
                    </div>

                    <div v-if="sellerStatusBadges(item).length" class="status-multi">
                      <h6>Status:</h6>
                      <span
                        v-for="status in sellerStatusBadges(item)"
                        :key="status.key"
                        class="status"
                        :class="status.key"
                      >
                        {{ status.label }}
                      </span>
                    </div>

                    <div class="market-project__buttons buttons-group">
                      <a
                        v-if="!item.saved"
                        href="#"
                        class="btn-action"
                        @click.stop.prevent="sellerSaveProject(item.id)"
                      >
                        <i class="icon-save"></i>
                        Save
                      </a>

                      <a
                        v-else
                        href="#"
                        class="btn-action btn-reject"
                        @click.stop.prevent="sellerUnsaveProject(item.id)"
                      >
                        <i class="icon-unsave"></i>
                        Unsave
                      </a>

                      <a
                        href="#"
                        class="btn-action"
                        @click.stop.prevent="goToProject(item)"
                      >
                        <i class="icon-info"></i>
                        See Details
                      </a>

                      <a
                        v-if="canSellerMessageProject(item)"
                        href="#"
                        class="btn-action"
                        role="button"
                        tabindex="0"
                        @click.stop.prevent="openProjectMessages($event, item)"
                      >
                        <i class="icon-chat"></i>
                        Send message
                      </a>

                      <a
                        v-if="Number(item.offers_allow) === 1 && Number(item.status) !== 2"
                        href="#"
                        class="btn-action"
                        @click.stop.prevent="makeAnOffer(item)"
                      >
                        <i class="icon-coffer"></i>
                        Make an Offer
                      </a>
                    </div>
                  </template>
                </div>
              </component>
            </div>

            <b-collapse
              v-if="isProjectExpandable(item)"
              :id="`collapseProject-${item.id}`"
              accordion="accordionOffers"
              :aria-labelledby="`headingOffers-${item.id}`"
              @show="onProjectCollapseShow(item)"
            >
              <div class="card-body" :class="{ offers: isBuyer, 'accordion-sublevel': isBuyer }">
                <template v-if="isBuyer">
                  <template v-if="hasSellerOffers(item)">
                    <div v-if="loadingProjectOffers[item.id]" class="d-flex justify-content-center p-3">
                      <div class="loader"></div>
                    </div>
                    <template v-else-if="projectOffersMap[item.id] && projectOffersMap[item.id].length">
                      <h3>
                        <strong>{{ projectOffersMap[item.id].length }}</strong>
                        {{ projectOffersMap[item.id].length === 1 ? 'offer' : 'offers' }} from Sellers
                      </h3>
                      <div class="accordion offers__box" :id="`accordionBuyerOffers${item.id}`">
                        <div
                          v-for="(offer, offerIdx) in projectOffersMap[item.id]"
                          :key="offer.id || offerIdx"
                          class="scroll-target-offer"
                          :data-offer-id="offer.id"
                        >
                          <OfferCard
                            :type="getOfferStatusType(offer)"
                            :offer="offer.project ? offer : { ...offer, project: item }"
                            :index="offerIdx"
                            @changeStatus="() => fetchProjectOffers(item)"
                            @sendMessage="(messageData) => handleSendOfferMessage(messageData, item)"
                            @sellerUpdateOffer="() => fetchProjectOffers(item)"
                            @sellerDeleteOffer="() => fetchProjectOffers(item)"
                          />
                        </div>
                      </div>
                    </template>
                    <div
                      v-else-if="projectOffersMap[item.id] && projectOffersMap[item.id].length === 0"
                      class="text-muted py-2"
                    >
                      <p>No offers yet for this project.</p>
                    </div>
                  </template>
                </template>

                <template v-else>
                  <div v-if="hasSellerOffers(item)">
                    <div v-if="loadingProjectOffers[item.id]" class="d-flex justify-content-center p-3">
                      <div class="loader"></div>
                    </div>
                    <template v-else-if="projectOffersMap[item.id] && projectOffersMap[item.id].length">
                      <div
                        v-for="(offer, offerIdx) in projectOffersMap[item.id]"
                        :key="offer.id || offerIdx"
                        class="mb-3 scroll-target-offer"
                        :data-offer-id="offer.id"
                      >
                        <OfferCard
                          :type="getOfferStatusType(offer)"
                          :offer="offer.project ? offer : { ...offer, project: item }"
                          :index="offerIdx"
                          @changeStatus="() => fetchProjectOffers(item)"
                          @sendMessage="(messageData) => handleSendOfferMessage(messageData, item)"
                          @sellerUpdateOffer="() => fetchProjectOffers(item)"
                          @sellerDeleteOffer="() => fetchProjectOffers(item)"
                        />
                      </div>
                    </template>
                    <div
                      v-else-if="projectOffersMap[item.id] && projectOffersMap[item.id].length === 0"
                      class="text-muted py-2"
                    >
                      <p class="mb-2">You don't have any offers for this project yet.</p>
                      <div v-if="Number(item.offers_allow) === 1 && Number(item.status) !== 2" class="market-project__buttons buttons-group">
                        <a
                          href="#"
                          class="btn-action"
                          @click.stop.prevent="makeAnOffer(item)"
                        >
                          <i class="icon-coffer"></i>
                          Make an Offer
                        </a>
                      </div>
                    </div>
                  </div>
                </template>

                <div
                  v-if="!isBuyer || getProjectMessagesCount(item) > 0"
                  class="project-messages-section mt-4"
                  :data-project-id="item.id"
                >
                  <h5 class="mb-2">Project messages</h5>
                  <ProjectMessagesPanel
                    :projectId="item.id"
                    :messages="getProjectMessages(item)"
                    :readOnly="!isBuyer && !canSellerMessageProject(item)"
                    @sent="onProjectMessageSent(item, $event)"
                  />
                </div>
              </div>
            </b-collapse>
          </div>
        </div>

        <div v-if="loadingProjects" class="d-flex justify-content-center p-2">
          <div class="loader"></div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import MatchmakingService from '@/services/MatchmakingService';
import { BModal, BCollapse } from 'bootstrap-vue';
import exception from '@/mixins/exception';
import OfferFormRequest from '@/components/matchmaking/OfferFormRequest.vue';
import OfferCard from '@/views/profile/Matchmaking/Seller/OfferCard.vue';
import ProjectMessagesPanel from '@/components/matchmaking/ProjectMessagesPanel.vue';
import {
  canSellerMessageProject,
  isSellerProjectMessage,
  projectMessagesCount,
} from '@/helpers/projectMessages';

export default {
  name: 'MatchmakingProjectsList',
  mixins: [exception],
  components: {
    BModal,
    BCollapse,
    OfferFormRequest,
    OfferCard,
    ProjectMessagesPanel,
  },
  props: {
    marketplaceProjects: {
      type: Array,
      required: true,
    },
    isFiltered: {
      type: Boolean,
      required: true,
    },
    loadingProjects: {
      type: Boolean,
      required: true,
    },
    marketplace: {
      type: Object,
      default: null,
    },
    marketplaces: {
      type: Array,
      required: true,
    },
    selectedCountries: {
      type: Array,
      required: true,
    },
    dynamycDropboxes: {
      type: Object,
      required: true,
    },
    dynamycInputs: {
      type: Object,
      required: true,
    },
    searchInput: {
      type: String,
      required: true,
    },
    page: {
      type: Number,
      required: true,
    },
    perPage: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      formId: '',
      selectedProjectId: 0,
      selectedMarketplaceId: 0,
      statusFilter: 'all',
      subscribeToFilter: false,
      subscriptionFeedback: '',
      subscriptionBusy: false,
      currentSubscriptionId: null,
      isHydratingSubscription: false,
      expandedProjectId: null,
      loadingProjectOffers: {},
      projectOffersMap: {},
    };
  },
  async created() {
    this.syncStatusFilterFromRoute();
    if (!this.isBuyer) {
      await this.syncSubscriptionCheckbox();
    }
  },
  watch: {
    // eslint-disable-next-line func-names
    '$route.query.status': function () {
      this.syncStatusFilterFromRoute();
    },
    // eslint-disable-next-line func-names
    '$route.params.type': function () {
      this.syncStatusFilterFromRoute();
    },
    marketplace: {
      deep: true,
      async handler() {
        if (!this.isBuyer) await this.syncSubscriptionCheckbox(true);
      },
    },
    selectedCountries: {
      deep: true,
      async handler() {
        if (!this.isBuyer) await this.syncSubscriptionCheckbox(true);
      },
    },
    async subscribeToFilter(val) {
      if (this.isBuyer) return;
      if (this.isHydratingSubscription) return;
      await this.toggleFilterSubscription(Boolean(val));
    },
    '$route.query.expandProject': {
      handler(projectId) {
        if (!projectId || this.isBuyer) return;
        const offerId = this.$route.query.expandOffer;
        this.scrollToAndExpandProject(projectId, offerId);
      },
    },
    marketplaceProjects: {
      handler(projects) {
        if (
          !this.isBuyer
          && this.$route.query.expandProject
          && projects
          && projects.length
        ) {
          this.$nextTick(() => {
            const offerId = this.$route.query.expandOffer;
            this.scrollToAndExpandProject(this.$route.query.expandProject, offerId);
          });
        }
      },
    },
  },
  computed: {
    isBuyer() {
      return this.$route.params.type === 'buyer';
    },
    showStatusFilter() {
      return true;
    },
    buyerStats() {
      const total = this.marketplaceProjects.length;
      const offersReceived = this.marketplaceProjects.reduce(
        (sum, p) => sum + Number(p.offers_count || 0),
        0,
      );
      const deadlinePast = this.marketplaceProjects.filter((p) => Number(p.status) === 4).length;

      return {
        total,
        offersReceived,
        deadlinePast,
      };
    },
    sellerStats() {
      return {
        total: this.marketplaceProjects.length,
      };
    },
    statusFilterOptions() {
      if (this.isBuyer) {
        const expiredCount = this.buyerStats.deadlinePast;

        return [
          { value: 'all', label: 'ALL', className: 'all' },
          { value: 'published', label: 'PUBLISHED', className: 'published' },
          { value: 'offered', label: 'OFFERED', className: 'offered' },
          { value: 'approved', label: 'APPROVED', className: 'approved' },
          {
            value: 'expired',
            label: expiredCount ? `${expiredCount} EXPIRED` : 'EXPIRED',
            className: 'expired',
          },
        ];
      }

      return [
        { value: 'all', label: 'ALL', className: 'all' },
        { value: 'offered', label: 'OFFERED', className: 'offered' },
        { value: 'saved', label: 'SAVED', className: 'saved' },
        { value: 'threads', label: 'MESSAGE THREADS', className: 'threads' },
        { value: 'approved', label: 'APPROVED', className: 'approved' },
      ];
    },
    filteredMarketplaceProjects() {
      const list = Array.isArray(this.marketplaceProjects) ? this.marketplaceProjects : [];
      const filter = (this.statusFilter || 'all').toLowerCase();

      if (filter === 'all') return list;

      const status = (p) => Number((p && p.status) || 0);

      if (this.isBuyer) {
        if (filter === 'published') return list.filter((p) => status(p) === 1);
        if (filter === 'offered') return list.filter((p) => Number(p.offers_count || 0) > 0);
        if (filter === 'approved') return list.filter((p) => status(p) === 2);
        if (filter === 'expired') return list.filter((p) => status(p) === 4);
        return list;
      }

      if (filter === 'offered') return list.filter((p) => this.hasSellerOffers(p));
      if (filter === 'saved') return list.filter((p) => !!p.saved);
      if (filter === 'threads') return list.filter((p) => this.getThreadsCount(p) > 0);
      if (filter === 'approved') return list.filter((p) => status(p) === 2);
      return list;
    },
    paginatedMarketplaceProjects() {
      return this.filteredMarketplaceProjects.slice(0, this.page * this.perPage);
    },
  },
  methods: {
    getCurrentFilterSubscriptionPayload() {
      const marketplaceId = Number(this.marketplace?.id || 0);
      if (!marketplaceId) return null;

      const selectedCountryName = Array.isArray(this.selectedCountries) && this.selectedCountries.length
        ? this.selectedCountries[0]
        : '';
      if (!selectedCountryName) {
        return {
          marketplace_id: marketplaceId,
        };
      }

      const availableCountries = Array.isArray(this.marketplace?.filters?.countries)
        ? this.marketplace.filters.countries
        : [];
      const normalizedSelected = String(selectedCountryName).trim().toLowerCase();
      const country = availableCountries.find((item) => {
        const candidates = [item?.name, item?.nicename, item?.iso]
          .filter(Boolean)
          .map((v) => String(v).trim().toLowerCase());
        return candidates.includes(normalizedSelected);
      });
      if (!country?.id) {
        return {
          marketplace_id: marketplaceId,
        };
      }

      return {
        marketplace_id: marketplaceId,
        country_id: Number(country.id),
      };
    },
    async findSubscriptionIdByFilter(payload) {
      const { data } = await MatchmakingService.marketplaceProjectSubscriptions();
      // eslint-disable-next-line no-nested-ternary
      const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      const payloadCountryId = payload?.country_id ?? null;
      const found = list.find((item) => {
        const itemMarketplaceId = item?.marketplace?.id ?? null;
        const itemCountryId = item?.country?.id ?? null;
        return (
          String(itemMarketplaceId ?? '') === String(payload.marketplace_id ?? '')
          && String(itemCountryId ?? '') === String(payloadCountryId ?? '')
        );
      });
      return found?.id || null;
    },
    async syncSubscriptionCheckbox(preserveCheckedState = false) {
      this.subscriptionFeedback = '';
      const payload = this.getCurrentFilterSubscriptionPayload();
      if (!payload) {
        this.currentSubscriptionId = null;
        if (!preserveCheckedState) {
          this.isHydratingSubscription = true;
          this.subscribeToFilter = false;
          this.$nextTick(() => {
            this.isHydratingSubscription = false;
          });
        }
        return;
      }

      try {
        this.subscriptionBusy = true;
        const foundId = await this.findSubscriptionIdByFilter(payload);
        this.currentSubscriptionId = foundId;
        if (foundId) {
          this.isHydratingSubscription = true;
          this.subscribeToFilter = true;
        } else if (!preserveCheckedState) {
          this.isHydratingSubscription = true;
          this.subscribeToFilter = false;
        } else if (this.subscribeToFilter) {
          await this.toggleFilterSubscription(true);
        }
      } catch (e) {
        this.currentSubscriptionId = null;
        if (!preserveCheckedState) {
          this.isHydratingSubscription = true;
          this.subscribeToFilter = false;
        }
      } finally {
        this.$nextTick(() => {
          this.isHydratingSubscription = false;
        });
        this.subscriptionBusy = false;
      }
    },
    async toggleFilterSubscription(shouldSubscribe) {
      const payload = this.getCurrentFilterSubscriptionPayload();
      if (!payload) {
        this.currentSubscriptionId = null;
        this.subscriptionFeedback = 'Select marketplace. Subscription will be saved automatically.';
        return;
      }

      this.subscriptionBusy = true;
      this.subscriptionFeedback = '';

      try {
        if (shouldSubscribe) {
          await MatchmakingService.createMarketplaceProjectSubscription(payload);
          this.currentSubscriptionId = await this.findSubscriptionIdByFilter(payload);
          this.subscriptionFeedback = 'Subscription saved.';
        } else if (this.currentSubscriptionId) {
          await MatchmakingService.deleteMarketplaceProjectSubscription(this.currentSubscriptionId);
          this.currentSubscriptionId = null;
          this.subscriptionFeedback = 'Subscription removed.';
        }
      } catch (e) {
        this.isHydratingSubscription = true;
        this.subscribeToFilter = !shouldSubscribe;
        this.$nextTick(() => {
          this.isHydratingSubscription = false;
        });
        this.subscriptionFeedback = 'Failed to update subscription.';
      } finally {
        this.subscriptionBusy = false;
      }
    },
    syncStatusFilterFromRoute() {
      const raw = (this.$route.query.status || 'all').toString().toLowerCase();
      const allowed = this.statusFilterOptions.map((o) => o.value);

      this.statusFilter = allowed.includes(raw) ? raw : 'all';
    },
    setStatusFilter(value) {
      const allowed = this.statusFilterOptions.map((o) => o.value);
      const next = allowed.includes(value) ? value : 'all';

      this.statusFilter = next;

      const query = { ...this.$route.query };
      if (next === 'all') {
        delete query.status;
      } else {
        query.status = next;
      }

      this.$router.replace({ query }).catch(() => {});
    },
    getThreadsCount(project) {
      const p = project || {};
      const messagesLen = Array.isArray(p.messages) ? p.messages.length : 0;
      return Math.max(
        Number(p.threads_count || 0),
        Number(p.messages_count || 0),
        Number(p.message_threads_count || 0),
        messagesLen,
      );
    },

    canSellerMessageProject(project) {
      return canSellerMessageProject(project);
    },

    isProjectExpandable(item) {
      if (!this.isBuyer) return true;
      return this.hasSellerOffers(item) || this.getProjectMessagesCount(item) > 0;
    },

    getProjectMessages(item) {
      if (!item || !Array.isArray(item.messages)) return [];
      return item.messages;
    },

    getProjectMessagesCount(item) {
      return projectMessagesCount(item);
    },

    projectMessagesButtonLabel(item) {
      const count = this.getProjectMessagesCount(item);
      if (count === 1) return '1 message from Sellers';
      return `${count} messages from Sellers`;
    },

    openProjectMessages(event, item) {
      const expandableId = item && item.id;
      if (!expandableId) return;

      const card = event && event.currentTarget
        ? event.currentTarget.closest('[data-project-id]')
        : null;
      const trigger = card
        ? card.querySelector(`[aria-controls="collapseProject-${expandableId}"]`)
        : null;
      if (trigger && trigger.getAttribute('aria-expanded') !== 'true') {
        trigger.click();
      } else {
        this.$root.$emit('bv::toggle::collapse', `collapseProject-${expandableId}`);
      }

      this.$nextTick(() => {
        setTimeout(() => {
          const section = this.$el
            && this.$el.querySelector(`.project-messages-section[data-project-id="${expandableId}"]`);
          if (section && typeof section.scrollIntoView === 'function') {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 250);
      });
    },

    onProjectMessageSent(item, message) {
      if (!item) return;

      const list = Array.isArray(item.messages) ? item.messages : [];
      if (message && typeof message === 'object') {
        this.$set(item, 'messages', [...list, message]);
      }

      if (isSellerProjectMessage(message)) {
        const baseCount = Number(item.messages_count);
        const nextCount = Number.isFinite(baseCount) ? baseCount + 1 : projectMessagesCount(item);
        this.$set(item, 'messages_count', nextCount);
      }
    },
    getCounterOffersCount(project) {
      const p = project || {};
      return Number(p.counter_offers_count || p.counteroffers_count || 0);
    },
    projectUrl(project) {
      const p = project || {};
      const iso = (p.company_country && (p.company_country.iso || p.company_country.code)) || 'xx';
      return `/profile/matchmaking${this.isBuyer ? '/buyer' : '/seller'}/project/${iso}-${p.id}`;
    },

    buyerStatusBadges(project) {
      const status = Number((project && project.status) || 0);
      const list = [];

      if (status === 2) {
        list.push({ key: 'approved', label: 'Approved' });
      } else if (status === 4) {
        list.push({ key: 'expired', label: 'Expired' });
      } else {
        list.push({ key: 'published', label: 'Published' });
      }

      if (this.hasProjectOffers(project)) {
        list.push({ key: 'offered', label: 'Offered' });
      }

      return list;
    },

    sellerStatusBadges(project) {
      const p = project || {};
      const status = Number(p.status);
      const list = [];

      if (status === 2) list.push({ key: 'approved', label: 'Approved' });
      if (status === 4) list.push({ key: 'expired', label: 'Expired' });
      if (p.saved) list.push({ key: 'saved', label: 'Saved' });
      if (this.hasProjectOffers(project)) list.push({ key: 'offered', label: 'Offered' });

      return list;
    },

    goToProject(project) {
      this.$router.push(this.projectUrl(project));
    },

    toggleProjectAccordion(event) {
      const outer = event.currentTarget && event.currentTarget.closest('.btn-accordion');
      if (outer) outer.click();
    },

    formatDeadline(deadline) {
      if (!deadline || deadline === '∞') return 'not specified';
      return deadline;
    },

    accordionClass(project) {
      const p = project || {};
      const status = Number(p.status);

      if (status === 2) return 'approved';
      if (status === 4) return 'expired';

      if (!this.isBuyer && p.saved) return 'saved';

      const offersCount = Number(p.offers_count || 0);
      if (offersCount > 0) return this.isBuyer ? 'published' : 'offered';

      return 'published';
    },

    statusLabel(project) {
      const p = project || {};
      const status = Number(p.status);

      if (status === 2) return 'Approved';
      if (status === 4) return 'Expired';
      if (p.saved) return 'Saved';
      return '';
    },

    statusClass(project) {
      const p = project || {};
      const status = Number(p.status);

      if (status === 2) return 'approved';
      if (status === 4) return 'expired';
      if (p.saved) return 'saved';
      return '';
    },

    hasProjectOffers(project) {
      if (!project) return false;
      const { id } = project;

      return Number(project.offers_count || 0) > 0
        || (Array.isArray(project.offers) && project.offers.length > 0)
        || (this.projectOffersMap[id] && this.projectOffersMap[id].length > 0);
    },

    projectDataList(project) {
      const p = project || {};
      const d = p.data;

      if (Array.isArray(d)) return d.filter(Boolean);
      if (d && typeof d === 'object') return Object.values(d).filter(Boolean);

      return [];
    },

    resolveDropboxLabel(values, key) {
      if (!values) return '';

      if (Array.isArray(values)) {
        const found = values.find((v) => v && String(v.id) === String(key));
        if (!found) return '';
        return found.name || found.value || String(key);
      }

      return values[key] || values[String(key)] || '';
    },

    formatCategoryValue(category) {
      if (!category) return '';

      if (category.type === 'dropbox') {
        const values = category.values || {};

        if (category.multiple) {
          const raw = category.initValue;
          // eslint-disable-next-line no-nested-ternary
          const ids = Array.isArray(raw) ? raw : (raw ? [raw] : []);
          const labels = ids
            .map((id) => this.resolveDropboxLabel(values, id))
            .filter(Boolean);

          return labels.join(', ');
        }

        if (category.initValue === null || category.initValue === undefined || category.initValue === '') {
          return '';
        }

        return this.resolveDropboxLabel(values, category.initValue);
      }

      if (category.initValue === null || category.initValue === undefined || category.initValue === '') {
        return '';
      }

      return String(category.initValue);
    },

    projectSpecs(project) {
      const p = project || {};
      const list = this.projectDataList(p);

      return list
        .map((c, idx) => {
          if (!c || !c.name) return null;
          const value = this.formatCategoryValue(c);
          if (!value) return null;
          return {
            key: `${p.id || 'p'}-${idx}`,
            label: c.name,
            value,
          };
        })
        .filter(Boolean);
    },

    onCreatedOffer(offerData) {
      const projectId = offerData?.project_id ?? offerData?.project?.id ?? this.selectedProjectId;
      if (!projectId || this.isBuyer) {
        this.closeModal();
        return;
      }

      const project = this.paginatedMarketplaceProjects.find(
        (p) => String(p.id) === String(projectId),
      );
      if (project && offerData) {
        const existing = this.projectOffersMap[projectId] || [];
        const newOffer = { ...offerData, project };
        this.$set(this.projectOffersMap, projectId, [...existing, newOffer]);
      }

      this.$emit('sellerCreatedOffer', { projectId, offerData });
      this.closeModal();

      const offerId = offerData?.id ?? offerData?.offer?.id;
      this.$nextTick(() => {
        this.scrollToAndExpandProject(projectId, offerId);
      });
    },

    onSaveOffer() {
      this.closeModal();
    },

    generateFormId() {
      this.formId = `${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`;
    },

    closeModal() {
      this.selectedProjectId = 0;
      this.selectedMarketplaceId = 0;
      if (this.$refs.offerFormRequest) this.$refs.offerFormRequest.hide();
    },

    makeAnOffer(project) {
      this.selectedProjectId = project.id;
      this.selectedMarketplaceId = project.marketplace
      && project.marketplace.id ? project.marketplace.id : 0;
      this.generateFormId();
      this.$refs.offerFormRequest.show();
    },

    async sellerSaveProject(projectId) {
      try {
        await MatchmakingService.sellerSaveProject({ project_id: projectId });
        this.$emit('toggleSavedProject', projectId);
      } catch (error) {
        this.handleException(error);
      }
    },

    async sellerUnsaveProject(projectId) {
      try {
        await MatchmakingService.sellerUnsaveProject({ project_id: projectId });
        this.$emit('toggleSavedProject', projectId);
      } catch (error) {
        this.handleException(error);
      }
    },

    unreadMessagesCount(projectId) {
      return this.$store.getters.getUnreadMarketplaceData
        && this.$store.getters.getUnreadMarketplaceData.buyer
        && this.$store.getters.getUnreadMarketplaceData.buyer.projects
        && this.$store.getters.getUnreadMarketplaceData.buyer.projects[projectId];
    },

    hasSellerOffers(project) {
      if (!project) return false;
      const { id } = project;
      return Number(project.offers_count || 0) > 0
        || (Array.isArray(project.offers) && project.offers.length > 0)
        || (this.projectOffersMap[id] && this.projectOffersMap[id].length > 0);
    },

    onProjectCollapseShow(project) {
      this.fetchProjectOffers(project);
    },

    async handleSendOfferMessage(messageData, project) {
      const { offerId, text } = messageData || {};
      if (!text || !offerId) return;
      try {
        const { data } = await MatchmakingService.marketplaceOfferMessage({
          offer_id: offerId,
          text,
        });
        const projectId = project && project.id;
        const offers = this.projectOffersMap[projectId];
        if (offers && Array.isArray(offers)) {
          const offer = offers.find((o) => String(o.id) === String(offerId));
          if (offer) {
            const messages = offer.messages || [];
            this.$set(offer, 'messages', [...messages, data]);
          }
        }
      } catch (e) {
        this.handleException(e);
      }
    },

    async fetchProjectOffers(project) {
      const id = project && project.id;
      if (!id) return;

      this.$set(this.loadingProjectOffers, id, true);
      try {
        const { data } = await MatchmakingService.projectOffers(id, this.isBuyer);
        let offers = [];
        if (Array.isArray(data)) {
          offers = data;
        } else if (data && Array.isArray(data.data)) {
          offers = data.data;
        }
        this.$set(this.projectOffersMap, id, offers.map((o) => (o.project ? o : { ...o, project })));
      } catch (e) {
        this.$set(this.projectOffersMap, id, []);
      } finally {
        this.$set(this.loadingProjectOffers, id, false);
      }
    },

    getOfferStatusType(offer) {
      if (!offer || !offer.status) return 'offered';
      const active = Array.isArray(offer.status)
        ? offer.status.find((s) => s && s.active)
        : null;
      return active ? String(active.name || 'offered').toLowerCase() : 'offered';
    },

    expandProjectCollapse(projectId) {
      const id = `collapseProject-${projectId}`;
      const collapseEl = document.getElementById(id);
      if (collapseEl && typeof window.$ !== 'undefined' && window.$.fn.collapse) {
        window.$(collapseEl).collapse('show');
        return;
      }
      this.$root.$emit('bv::show::collapse', id);
      const card = this.$el && this.$el.querySelector(`[data-project-id="${projectId}"]`);
      const btn = card && card.querySelector(`[aria-controls="${id}"]`);
      if (btn && btn.getAttribute('aria-expanded') !== 'true') {
        btn.click();
      }
    },

    async scrollToAndExpandProject(projectId, offerId) {
      if (!projectId) return;

      const project = this.paginatedMarketplaceProjects.find(
        (p) => String(p.id) === String(projectId),
      );
      if (!project) return;

      const hasOffers = this.hasSellerOffers(project)
        || (this.projectOffersMap[projectId] && this.projectOffersMap[projectId].length > 0);
      if (hasOffers) {
        if (!this.projectOffersMap[projectId] || !this.projectOffersMap[projectId].length) {
          await this.fetchProjectOffers(project);
        }
      }
      if (this.isProjectExpandable(project)) {
        await this.$nextTick();
        await this.$nextTick();
        this.expandProjectCollapse(projectId);
        [150, 350, 600].forEach((delay) => {
          setTimeout(() => {
            const card = this.$el && this.$el.querySelector(`[data-project-id="${projectId}"]`);
            const btn = card && card.querySelector(`[aria-controls="collapseProject-${projectId}"]`);
            if (btn && btn.getAttribute('aria-expanded') !== 'true') {
              this.expandProjectCollapse(projectId);
            }
          }, delay);
        });
      } else {
        this.expandedProjectId = projectId;
      }

      await this.$nextTick();
      const el = this.$el && this.$el.querySelector(`[data-project-id="${projectId}"]`);
      if (el && !offerId) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      if (offerId) {
        const scrollToOffer = () => {
          const offerEl = this.$el && this.$el.querySelector(`[data-offer-id="${offerId}"]`);
          if (!offerEl) return;
          offerEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        };
        setTimeout(scrollToOffer, 550);
        setTimeout(scrollToOffer, 900);
      }

      const q = { ...this.$route.query };
      delete q.expandProject;
      delete q.expandOffer;
      if (Object.keys(q).length !== Object.keys(this.$route.query).length) {
        this.$router.replace({ query: q }).catch(() => {});
      }
    },

    async openSendMessage(project) {
      const id = project && project.id;
      if (!id) return;

      const el = this.$el && this.$el.querySelector(`[data-project-id="${id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      const hasOffers = this.hasSellerOffers(project)
        || (this.projectOffersMap[id] && this.projectOffersMap[id].length > 0);

      if (hasOffers) {
        await this.fetchProjectOffers(project);
        await this.$nextTick();
        this.$root.$emit('bv::show::collapse', `collapseProject-${id}`);
        await this.$nextTick();
        const offers = this.projectOffersMap[id] || [];
        const firstOffer = offers[0];
        if (firstOffer && firstOffer.id) {
          const expandOffer = () => {
            const collapseEl = document.getElementById(`collapse-offer${firstOffer.id}`);
            if (collapseEl && typeof window.$ !== 'undefined' && window.$.fn.collapse) {
              window.$(collapseEl).collapse('show');
              return;
            }
            const trigger = this.$el.querySelector(
              `[data-target="#collapse-offer${firstOffer.id}"], [data-bs-target="#collapse-offer${firstOffer.id}"]`,
            );
            if (trigger) trigger.click();
          };
          setTimeout(expandOffer, 450);
          setTimeout(expandOffer, 800);
        }
      } else if (String(this.expandedProjectId) === String(id)) {
        this.expandedProjectId = null;
      } else {
        this.expandedProjectId = id;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.link {
  text-decoration: underline;
  font-weight: 700;
}

.project-card {
  position: relative;
}

.message_badge {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 20px;
  height: 20px;

  position: absolute;
  top: 5px;
  left: 5px;

  background-color: red;
  border-radius: 50%;

  z-index: 2;

  color: #fff;
  font-size: 12px;
  font-weight: 600;

  @media (max-width: 992px) {
    left: 10px;
  }
}

.subscribe__feedback {
  margin-top: 8px;
}

.project-card--no-accordion .project-card__plain {
  cursor: default;
  width: 100%;
  text-align: left;
  padding: 1rem;
}

.project-card--no-accordion .btn-accordion::after,
.project-card__plain.btn-accordion::after {
  display: none;
}

.scroll-target-offer {
  scroll-margin-top: 100px;
}

.market-project__info {
  text-align: left;
}

.market-project__buttons.buttons-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;

  & a {
    margin-left: 0;
  }

  .btn-action {
    height: 40px !important;
    min-height: 40px !important;
    padding: 10px 22px !important;
    font-size: 14px !important;
    line-height: 40px !important;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  }
}

::v-deep .card-body .market-project__buttons.buttons-group .btn-action {
  height: 40px !important;
  min-height: 40px !important;
  padding: 10px 22px !important;
  font-size: 14px !important;
  line-height: 40px !important;
}
</style>
